import { useState, useEffect } from 'react';

interface FontLoadingState {
  interLoaded: boolean;
  montserratLoaded: boolean;
  allFontsLoaded: boolean;
}

/**
 * Hook personalizado para optimizar la carga de fuentes
 * Detecta cuando las fuentes Inter y Montserrat están completamente cargadas
 */
export const useFontLoading = (): FontLoadingState => {
  const [fontState, setFontState] = useState<FontLoadingState>({
    interLoaded: false,
    montserratLoaded: false,
    allFontsLoaded: false,
  });

  useEffect(() => {
    // Función para verificar si una fuente está cargada
    const checkFontLoaded = (fontFamily: string): Promise<boolean> => {
      return new Promise((resolve) => {
        if (document.fonts && document.fonts.check) {
          // Navegadores modernos con Font Loading API
          const isLoaded = document.fonts.check(`12px "${fontFamily}"`);
          resolve(isLoaded);
        } else {
          // Fallback para navegadores antiguos
          resolve(true);
        }
      });
    };

    // Función para esperar a que una fuente se cargue
    const waitForFont = (): Promise<void> => {
      return new Promise((resolve) => {
        if (document.fonts && document.fonts.ready) {
          // Usar Font Loading API si está disponible
          document.fonts.ready.then(() => {
            resolve();
          });
        } else {
          // Fallback: esperar un tiempo razonable
          setTimeout(resolve, 1000);
        }
      });
    };

    // Función principal para cargar fuentes
    const loadFonts = async () => {
      try {
        // Verificar fuentes del sistema primero
        const interSystem = await checkFontLoaded('Inter');
        const montserratSystem = await checkFontLoaded('Montserrat');

        if (interSystem && montserratSystem) {
          // Las fuentes ya están disponibles en el sistema
          setFontState({
            interLoaded: true,
            montserratLoaded: true,
            allFontsLoaded: true,
          });
          return;
        }

        // Esperar a que las fuentes web se carguen
        await waitForFont();
        await waitForFont();

        // Verificar que las fuentes estén realmente cargadas
        const interWeb = await checkFontLoaded('Inter');
        const montserratWeb = await checkFontLoaded('Montserrat');

        setFontState({
          interLoaded: interWeb,
          montserratLoaded: montserratWeb,
          allFontsLoaded: interWeb && montserratWeb,
        });

        // Agregar clase CSS cuando las fuentes estén cargadas
        if (interWeb && montserratWeb) {
          document.documentElement.classList.add('fonts-loaded');
          document.body.classList.add('fonts-loaded');
        }
      } catch (error) {
        console.warn('Error loading fonts:', error);
        // En caso de error, asumir que las fuentes están cargadas
        setFontState({
          interLoaded: true,
          montserratLoaded: true,
          allFontsLoaded: true,
        });
      }
    };

    // Iniciar carga de fuentes
    loadFonts();

    // Cleanup function
    return () => {
      document.documentElement.classList.remove('fonts-loaded');
      document.body.classList.remove('fonts-loaded');
    };
  }, []);

  return fontState;
};

/**
 * Hook simplificado para verificar si todas las fuentes están cargadas
 */
export const useFontsReady = (): boolean => {
  const { allFontsLoaded } = useFontLoading();
  return allFontsLoaded;
};

/**
 * Hook para obtener el estado individual de cada fuente
 */
export const useIndividualFonts = () => {
  const { interLoaded, montserratLoaded } = useFontLoading();
  return { interLoaded, montserratLoaded };
};
